import {
  Component,
  inject,
  signal,
  computed,
  OnInit,
  input,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AppEnvironment } from 'sham-components/services/core/app-environment';
import {
  ProductReviewsManager,
  ProductReview,
  ReviewSummary,
} from '@/app/pages/product-details/product-reviews/product-reviews-manager';
import { ShamRatingStarIcon, ShamLeadingIcon } from 'sham-components/icons';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-product-reviews',
  imports: [
    CommonModule,
    ShamRatingStarIcon,
    ShamLeadingIcon,
    RouterLink,
    TranslateModule,
  ],
  templateUrl: './product-reviews.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductReviews implements OnInit {
  private reviewsManager = inject(ProductReviewsManager);
  readonly appEnv = inject(AppEnvironment);
  showPageDropdown = signal(false);
  pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  productId = input.required<string>();
  productSlug = input.required<string>();
  reviewsAverage = input.required<number>();
  reviewsCount = input.required<number>();

  isLoading = computed(() => this.reviewsManager.loading());
  reviews = computed(() => this.reviewsManager.reviewsList());
  summary = computed(() => this.reviewsManager.reviewSummary());
  hasMore = computed(() => this.reviewsManager.hasMore());
  currentPage = computed(() => this.reviewsManager.currentPage());
  totalPages = computed(() => this.reviewsManager.totalPages());

  showFilterDropdown = signal(false);
  selectedFilter = signal<number | null>(null);

  constructor() {
    effect(() => {
      const id = this.productId();
      if (id) {
        this.reviewsManager.resetState();
        this.loadReviews(id);
      }
    });
  }

  ngOnInit() {
    // Initial load is handled by the effect
  }

  loadReviews(productId: string, page: number = 1) {
    // Was `minRating`, which doesn't exist on ReviewsFilter — API only supports exact `rating` match.
    const filter = this.selectedFilter()
      ? { rating: this.selectedFilter()! }
      : undefined;
    firstValueFrom(this.reviewsManager.loadProductReviews(productId, page, filter))
      .then((response: any) => {
        this.reviewsManager.setReviewsData(response);
      })
      .catch((error) => {
        console.error('Error loading reviews:', error);
      });
  }

  loadMore() {
    if (!this.hasMore() || this.isLoading()) return;

    const observable = this.reviewsManager.loadMoreReviews();
    if (!observable) return;

    firstValueFrom(observable)
      .then((response: any) => {
        this.reviewsManager.appendReviews(response);
      })
      .catch((error) => {
        console.error('Error loading more reviews:', error);
      });
  }

  toggleFilterDropdown() {
    this.showFilterDropdown.update((v) => !v);
  }

  filterByRating(rating: number | null) {
    this.selectedFilter.set(rating);
    this.showFilterDropdown.set(false);
    const filter = rating ? { rating } : {};
    this.reviewsManager.applyFilter(filter);
    this.loadReviews(this.productId(), 1);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages()) return;
    this.loadReviews(this.productId(), page);
  }

  goToPreviousPage() {
    const current = this.currentPage();
    if (current > 1) {
      this.goToPage(current - 1);
    }
  }

  goToNextPage() {
    const current = this.currentPage();
    if (current < this.totalPages()) {
      this.goToPage(current + 1);
    }
  }

  getStarArray(rating: number): number[] {
    const filled = Math.max(0, Math.min(5, Math.round(rating)));
    return Array(filled).fill(0);
  }

  roundRating(rating: number): number {
    return Math.round(rating);
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }
}
